import { Button } from '@mui/material';
import React, { FC } from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';

import { getOpenModalUrl } from '../../../containers/modals/helpers';
import { Message } from '../../../generated/graphql';
import MuiIcon from '../../MUI/MuiIcon';

import { getButtonArchiveElements } from './helpers';
import * as Styled from './styled';

export type Props = {
  isArchived?: boolean;
  messageId: Message['id'];
  shouldShowContent: boolean;
  shouldUseIcon?: boolean;
};

/**
 * Message section which returns archive button if user is authorized to archive the message.
 * If button is clicked, confirmation modal pops up.
 *
 * @param props                   Props passed to the component
 * @param props.isArchived        Whether the message is archived or not; optional
 * @param props.messageId         Id of message
 * @param props.shouldShowContent If archive button should be visible, depending on permission
 * @param props.shouldUseIcon     If to use a archive icon instead of a archive button
 * @returns                       The component itself or null
 */
const MessageArchive: FC<Props> = ({
  isArchived = false,
  messageId,
  shouldUseIcon = false,
  shouldShowContent,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { buttonLabel, modalId } = getButtonArchiveElements(isArchived);

  if (shouldShowContent === false) {
    return null;
  }

  const modalUrl = getOpenModalUrl(location, modalId, {
    messageId,
  });

  /**
   * Handles the archive button click.
   * Navigates to the same page with modal parameters in the URL,
   * which triggers the confirmation modal without reloading the page.
   */
  const handleClick = () => {
    navigate(modalUrl, { replace: false });
  };

  if (shouldUseIcon) {
    return (
      <Button
        aria-label={buttonLabel}
        color="primary"
        component={Link}
        to={modalUrl}
      >
        <MuiIcon icon="archive" />
      </Button>
    );
  }

  return (
    <Styled.MuiButton
      aria-label={buttonLabel}
      color="primary"
      onClick={handleClick}
      startIcon={<MuiIcon icon={isArchived ? 'unarchive' : 'archive'} />}
      style={{ justifyContent: 'flex-start' }}
    >
      {buttonLabel}
    </Styled.MuiButton>
  );
};

export default MessageArchive;
